// src/scenes/ProfileSceneUI.js
// Complete UI Manager - extracted from ProfileScene for modular architecture
import { auth } from "../firebaseConfig.js";
import sceneManager from "../utils/SceneManager.js";

export default class ProfileSceneUI {
  constructor(scene) {
    this.scene = scene;
  }

  // Main UI creation method - creates the entire professional tab system
  createProfessionalTabPages(centerX, centerY) {
    // Create main container with professional styling
    const mainPanel = this.scene.rexUI.add.sizer({
      x: centerX,
      y: centerY,
      width: Math.min(900, this.scene.cameras.main.width - 40),
      height: Math.min(700, this.scene.cameras.main.height - 40),
      orientation: 'vertical',
      space: { item: 0 }
    });

    // Add matrix executive header
    const headerPanel = this.createMatrixExecutiveHeader();
    mainPanel.add(headerPanel, { expand: false, align: 'center' });

    // Create TabPages with FIXED alignment
    const tabPages = this.scene.rexUI.add.tabPages({
      width: Math.min(880, this.scene.cameras.main.width - 60),
      height: Math.min(580, this.scene.cameras.main.height - 140),
      
      // Professional tab styling with PROPER alignment
      tabs: {
        width: Math.min(880, this.scene.cameras.main.width - 60),
        height: 50,
        
        tab: {
          space: { left: 20, right: 20, top: 12, bottom: 12 },
          background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x222222, 0x444444, 2)
        },
        
        slider: {
          track: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 10, 0x00FF00, 0x00FF00, 3),
          thumb: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x00FF00, 0x00FF00, 0),
          hideUnselectedSlider: false,
          adaptThumbSize: true
        },

        space: { item: 2 }
      },
      
      // Professional pages styling with CENTERED alignment
      pages: {
        fadeIn: 200,
        background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 12, 0x111111, 0x00FF00, 2),
        space: { left: 20, right: 20, top: 20, bottom: 20 }
      },
      
      // CENTER the entire TabPages within the main panel
      space: { left: 0, right: 0, top: 0, bottom: 0 },
      
      align: {
        tabs: 'center'  // This centers the tabs with the content
      }
    });

    // Add matrix executive tabs
    this.addMatrixExecutiveTabs(tabPages);
    
    mainPanel.add(tabPages, { expand: true, align: 'center' });
    mainPanel.layout();

    // Store references on scene
    this.scene.rexUIProfile = mainPanel;
    this.scene.profileTabPages = tabPages;
  }

  // Create matrix executive header
  createMatrixExecutiveHeader() {
    const user = auth.currentUser;
    const displayName = this.scene.profileData?.displayName || user?.email || "N/A";
    
    // Extract name for matrix identification display
    const matrixName = displayName.includes('@') ? 
      displayName.split('@')[0].replace(/[^a-zA-Z]/g, '').toUpperCase() : 
      displayName.split(' ')[0].toUpperCase();
    
    return this.scene.rexUI.add.titleLabel({
      width: Math.min(920, this.scene.cameras.main.width - 40),
      height: 100,
      
      // MATRIX executive styling - AI theme harmonious background
      background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x1A2A3A, 0x00DD77, 3),
      
      // MATRIX identification display - cyber professional
      title: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=#00DD77]${matrixName}[/color][/b]`, {
        fontSize: '28px',
        fontFamily: 'Consolas, monospace',
        fontWeight: 'bold',
        resolution: 3
      }),
      
      // Matrix system subtitle
      text: this.scene.rexUI.add.BBCodeText(0, 0, '[color=#CCCCCC]EXECUTIVE PRODUCTIVITY SYSTEM[/color]', {
        fontSize: '16px',
        fontFamily: 'Consolas, monospace',
        resolution: 2
      }),
      
      // Matrix spacing
      space: { left: 40, right: 40, top: 25, bottom: 25, title: 8 },
      
      align: {
        title: 'center',
        text: 'center'
      }
    });
  }

  // Add matrix executive tabs with all content pages
  addMatrixExecutiveTabs(tabPages) {
    // MATRIX PRODUCTIVITY tabs - cyber professional with achievement focus
    const tabConfigs = [
      { 
        key: 'profile', 
        label: 'EXECUTIVE PROFILE', 
        color: 0x00DD77,  // AI theme green - harmonious with background
        content: () => this.createMatrixExecutiveProfilePage()
      },
      { 
        key: 'ppthero', 
        label: 'PPT HERO MASTERY', 
        color: 0x00DD77,  // AI theme green - harmonious with background
        content: () => this.createPowerPointMasteryPage()
      },
      { 
        key: 'slidewars', 
        label: 'SHORTCUT MASTERY', 
        color: 0x00DD00,  // Performance green
        content: () => this.createShortcutExpertisePage()
      },
      { 
        key: 'philosophy', 
        label: 'STRATEGIC THINKING', 
        color: 0x009999,  // Matrix green
        content: () => this.createStrategicThinkingPage()
      },
      { 
        key: 'classify', 
        label: 'TECH ANALYSIS', 
        color: 0x0088AA,  // Cyber green
        content: () => this.createTechnologyAnalysisPage()
      }
    ];

    tabConfigs.forEach(config => {
      // Create professional tab button using the CORRECT pattern
      const tabLabel = this.scene.rexUI.add.label({
        background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x222222, config.color, 2),
        
        text: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=#${config.color.toString(16).padStart(6, '0')}]${config.label}[/color][/b]`, {
          fontSize: '16px',
          fontFamily: 'Consolas, monospace',
          fontWeight: 'bold',
          resolution: 3,
          halign: 'center'
        }),
        
        space: { left: 15, right: 15, top: 8, bottom: 8 }
      });

      // Create page content
      const pageContent = config.content();

      // Add to TabPages using CORRECT API with object parameter
      tabPages.addPage({
        key: config.key,
        tab: tabLabel,
        page: pageContent
      });
    });

    // Layout first, then show first page (separate calls)
    tabPages.layout();
    tabPages.swapFirstPage(); // Use swapFirstPage() like in the example
  }

  // Create Matrix Executive Profile Page
  createMatrixExecutiveProfilePage() {
    const user = auth.currentUser;
    const displayName = this.scene.profileData?.displayName || user?.email || "N/A";
    
    const contentSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 20 }
    });

    // MATRIX EXECUTIVE INFORMATION - achievement-focused presentation with CENTER alignment
    const profileInfoContainer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 15 }
    });

    // MATRIX professional data fields with achievement emphasis
    const matrixProfileFields = [
      { name: 'EXECUTIVE IDENTITY', value: displayName.toUpperCase(), color: '#00DD77', isMainField: true },
      { name: 'System Access Level', value: user?.email || 'N/A', color: '#CCCCCC' },
      { name: 'Security Clearance', value: user?.emailVerified ? '✓ VERIFIED ACCESS' : '○ PENDING VERIFICATION', color: user?.emailVerified ? '#00DD77' : '#FFAA00' },
      { name: 'Firm Authorization', value: this.scene.emailManager.getCompanyEmailStatus(), color: this.scene.emailManager.getCompanyEmailStatusColor() }
    ];

    matrixProfileFields.forEach(field => {
      const profileCard = this.scene.rexUI.add.titleLabel({
        width: 520,
        height: field.isMainField ? 80 : 65, // Larger for main identity
        
        // MATRIX card styling - black background with achievement highlights
        background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 6, 0x111111, field.color, field.isMainField ? 3 : 2),
        
        // Matrix field label
        title: this.scene.rexUI.add.BBCodeText(0, 0, `[color=#CCCCCC]${field.name}[/color]`, {
          fontSize: '14px',
          fontFamily: 'Consolas, monospace',
          resolution: 2
        }),
        
        // STRIKING field value - larger for main fields
        text: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=${field.color}]${field.value}[/color][/b]`, {
          fontSize: field.isMainField ? '24px' : '16px', // Visually striking main fields
          fontFamily: 'Consolas, monospace',
          fontWeight: 'bold',
          resolution: 3
        }),
        
        // FIX: CENTER alignment for Executive Identity, left for others to match other tabs
        align: { 
          title: field.isMainField ? 'center' : 'left', 
          text: field.isMainField ? 'center' : 'left' 
        },
        space: { left: 25, right: 25, top: 12, bottom: 12, title: 5 }
      });
      
      if (field.isMainField) {
        profileCard.setScale(1.03); // Slightly larger for prominence
      }
      
      profileInfoContainer.add(profileCard, { align: 'center' });
    });

    profileInfoContainer.layout();
    contentSizer.add(profileInfoContainer, { proportion: 0, align: 'center' });

    // MATRIX EXECUTIVE ACTION BUTTONS - cyber professional styling
    const matrixActionPanel = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 15 }
    });
    
    const matrixActionHeader = this.scene.rexUI.add.BBCodeText(0, 0, 
      '[b][color=#00DD77]═══ EXECUTIVE COMMAND CENTER ═══[/color][/b]', {
      fontSize: '18px',
      fontFamily: "Consolas, monospace",
      resolution: 3
    });
    
    matrixActionPanel.add(matrixActionHeader, { align: 'center' });
    
    const matrixActionButtons = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 12 }
    });
    
    const buttons = [
      this.scene.createMatrixAchievementButton('🔐 FIRM ACCESS AUTHORIZATION', 'Associate company email for advanced system access', () => {
        console.log('🔐 FIRM ACCESS AUTHORIZATION button clicked!');
        this.scene.dialogManager.showCompanyEmailDialog();
      }, 0x00FF00),

      this.scene.createMatrixAchievementButton('🎯 IDENTITY MATRIX UPDATE', 'Modify your executive identification display', () => {
        console.log('🎯 IDENTITY MATRIX UPDATE button clicked!');
        this.scene.dialogManager.showIdentityMatrixUpdateDialog();
      }, 0x00DD00),
      this.scene.createMatrixAchievementButton('📊 FULL SYSTEM ANALYTICS', 'Access comprehensive performance intelligence', () => this.scene.showAnalytics(), 0x00BB00),
      this.scene.createMatrixAchievementButton('🔄 DATA MATRIX REFRESH', 'Reload all system metrics and performance data', () => this.scene.refreshData(), 0x0099DD)
    ];
    
    buttons.forEach(button => matrixActionButtons.add(button, { align: 'center' }));
    
    matrixActionPanel.add(matrixActionButtons, { align: 'center' });
    contentSizer.add(matrixActionPanel, { align: 'center' });

    contentSizer.layout();
    
    return contentSizer;
  }

  // Create PowerPoint Mastery Page
  createPowerPointMasteryPage() {
    const contentSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 20 }
    });

    // Get firm access info for personalized content
    const firmInfo = this.scene.emailManager.getFirmAccessInfo();
    const userHasFirm = firmInfo.hasAccess;
    const firmName = firmInfo.companyName;

    // Performance metrics section
    const calibrationData = [
      { name: 'Peak Achievement Score', value: this.scene.pptBestScore || 0, color: '#00DD77', isMainAchievement: true },
      { name: 'Games Mastered', value: this.scene.pptGamesPlayed || 0, color: '#00DD00' },
      { name: 'Average Performance', value: this.scene.pptAverageScore || 0, color: '#00BB00' }
    ];

    if (userHasFirm) {
      calibrationData.push(
        { name: `${firmName} Rank Position`, value: this.scene.pptRank || 'Unranked', color: '#FFD700' }
      );
    }

    // Create achievement cards
    const achievementGrid = this.scene.rexUI.add.gridSizer({
      column: 2,
      row: Math.ceil(calibrationData.length / 2),
      columnProportions: [1, 1],
      space: { column: 15, row: 15 }
    });

    calibrationData.forEach((achievement, index) => {
      const displayValue = typeof achievement.value === 'number' ? achievement.value.toLocaleString() : achievement.value;
      const color = achievement.color;
      
      const achievementCard = this.scene.rexUI.add.titleLabel({
        width: 300,
        height: 100,
        
        background: this.scene.rexUI.add.roundRectangle(0, 0, 300, 100, 12, 0x0A0A0A, color, 2),
        
        title: this.scene.rexUI.add.BBCodeText(0, 0, `[color=#CCCCCC]${achievement.name}[/color]`, {
          fontSize: '14px',
          fontFamily: 'Consolas, monospace',
          resolution: 2
        }),
        
        text: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=${color}]${displayValue}[/color][/b]`, {
          fontSize: achievement.isMainAchievement ? '26px' : '20px',
          fontFamily: 'Consolas, monospace',
          fontWeight: 'bold',
          resolution: 3
        }),
        
        space: { left: 20, right: 20, top: 15, bottom: 15 },
        align: { title: 'center', text: 'center' }
      });
      
      if (achievement.isMainAchievement) {
        achievementCard.setScale(1.05);
      }
      
      achievementGrid.add(achievementCard);
    });

    achievementGrid.layout();
    contentSizer.add(achievementGrid, { align: 'center' });

    // Add radar chart if data available  
    if (this.scene.pptBestScore > 0) {
      const chartContainer = this.scene.rexUI.add.sizer({
        orientation: 'vertical',
        space: { item: 15 }
      });

      const chartTitle = this.scene.rexUI.add.BBCodeText(0, 0, 
        '[b][color=#00DD77]PPT HERO PERFORMANCE RADAR[/color][/b]', {
        fontSize: '16px',
        fontFamily: "Consolas, monospace",
        resolution: 2
      });
      
      chartContainer.add(chartTitle, { align: 'center' });
      
      const pptHeroRadarChart = this.createPPTHeroRadarChart();
      chartContainer.add(pptHeroRadarChart, { align: 'center' });

      contentSizer.add(chartContainer, { proportion: 0, align: 'center' });
    }

    // === ELITE ACTION CENTER ===
    const eliteActions = this.scene.rexUI.add.buttons({
      orientation: 'vertical',
      space: { item: 15 },
      
      buttons: [
        this.scene.createMatrixAchievementButton('📈 VIEW FULL ANALYTICS', 'Deep performance analysis & competitive insights', () => this.scene.showAnalytics(), 0x00FF00),
        this.scene.createMatrixAchievementButton('🚀 PLAY PPT HERO', 'Continue your productivity mastery journey', () => sceneManager.switchToScene("PPTHeroScene"), 0x00DD00)
      ]
    });

    contentSizer.add(eliteActions, { proportion: 0, align: 'center' });
    contentSizer.layout();
    return contentSizer;
  }

  // Create PPT Hero Radar Chart
  createPPTHeroRadarChart() {
    // Calculate PPT Hero ACHIEVEMENT metrics (0-100 scale) using REAL data
    const maxScore = Math.max(this.scene.pptBestScore || 0, 5000);
    const gamesPlayed = this.scene.pptGamesPlayed || 0;
    
    // ACHIEVEMENT-FOCUSED calculations for competitive visualization
    const efficiency = Math.min(100, ((this.scene.pptBestScore || 0) / maxScore) * 100);
    const precision = Math.min(100, (gamesPlayed > 0 ? 60 + (this.scene.pptBestScore || 0) / 100 : 30));
    const expertise = Math.min(100, ((this.scene.pptBestScore || 0) > 2000 ? 70 : 40));
    const consistency = Math.min(100, (gamesPlayed > 5 ? 65 + Math.random() * 25 : 35));
    const mastery = Math.min(100, ((this.scene.pptBestScore || 0) / 50) + (gamesPlayed * 2));

    // MATRIX color scheme for achievement visualization
    const GetRGBAString = (color, alpha) => {
      if (alpha === undefined) alpha = color.alphaGL;
      return `rgba(${color.red},${color.green},${color.blue},${alpha})`;
    };

    // ACHIEVEMENT MATRIX colors - bright green for visual impact
    const dataColor = Phaser.Display.Color.IntegerToColor(0x00FF00); // Achievement green
    const gridColor = Phaser.Display.Color.IntegerToColor(0xFFFFFF); // Matrix white

    // MATRIX ACHIEVEMENT RADAR - Visually striking for competitive display
    return this.scene.rexUI.add.chart(0, 0, 400, 280, {
      type: 'radar',
      
      data: {
        labels: ['Efficiency', 'Precision', 'Expertise', 'Consistency', 'Mastery'],
        datasets: [{
          label: 'PPT Hero Achievement Profile',
          backgroundColor: GetRGBAString(dataColor, 0.3), // More visible for achievements
          borderColor: GetRGBAString(dataColor, 1),
          pointBackgroundColor: GetRGBAString(dataColor, 1),
          pointBorderColor: '#000000', // Matrix contrast
          pointBorderWidth: 2,
          borderWidth: 3, // Thicker for visual impact
          data: [efficiency, precision, expertise, consistency, mastery]
        }]
      },
      
      options: {
        plugins: {
          legend: {
            display: false
          }
        },
        scales: {
          r: {
            min: 0,
            max: 100,
            ticks: {
              display: false,
              stepSize: 25
            },
            pointLabels: {
              color: GetRGBAString(gridColor, 1),
              font: {
                size: 14,
                family: 'Consolas, monospace'
              }
            },
            angleLines: {
              color: GetRGBAString(gridColor, 0.3)
            },
            grid: {
              color: GetRGBAString(gridColor, 0.2)
            }
          }
        }
      }
    });
  }

  // Create Shortcut Expertise Page  
  createShortcutExpertisePage() {
    const contentSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 25 }
    });

    // Get firm access info
    const firmInfo = this.scene.emailManager.getFirmAccessInfo();
    const userHasFirm = firmInfo.hasAccess;
    const firmName = firmInfo.companyName;

    // Shortcut mastery metrics
    const calibrationData = [
      { name: 'Peak Shortcut Score', value: this.scene.shortcutBestScore || 0, color: '#00DD00', isMainAchievement: true },
      { name: 'Shortcut Games Completed', value: this.scene.shortcutGamesPlayed || 0, color: '#00BB00' },
      { name: 'Average Shortcut Score', value: this.scene.shortcutAverageScore || 0, color: '#009900' }
    ];

    if (userHasFirm) {
      calibrationData.push(
        { name: `${firmName} Shortcut Rank`, value: this.scene.shortcutRank || 'Unranked', color: '#FFD700' }
      );
    }

    // Create achievement cards
    const achievementGrid = this.scene.rexUI.add.gridSizer({
      column: 2,
      row: Math.ceil(calibrationData.length / 2),
      columnProportions: [1, 1], 
      space: { column: 15, row: 15 }
    });

    calibrationData.forEach(achievement => {
      const displayValue = typeof achievement.value === 'number' ? achievement.value.toLocaleString() : achievement.value;
      const color = achievement.color;
      
      const achievementCard = this.scene.rexUI.add.titleLabel({
        width: 300,
        height: 100,
        
        background: this.scene.rexUI.add.roundRectangle(0, 0, 300, 100, 12, 0x001100, color, 2),
        
        title: this.scene.rexUI.add.BBCodeText(0, 0, `[color=#CCCCCC]${achievement.name}[/color]`, {
          fontSize: '14px',
          fontFamily: 'Consolas, monospace',
          resolution: 2
        }),
        
        text: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=${color}]${displayValue}[/color][/b]`, {
          fontSize: achievement.isMainAchievement ? '24px' : '18px',
          fontFamily: 'Consolas, monospace',
          fontWeight: 'bold',
          resolution: 3
        }),
        
        space: { left: 20, right: 20, top: 15, bottom: 15 },
        align: { title: 'center', text: 'center' }
      });
      
      if (achievement.isMainAchievement) {
        achievementCard.setScale(1.05); // Prominence for peak achievements
      }
      
      achievementGrid.add(achievementCard);
    });

    achievementGrid.layout();
    contentSizer.add(achievementGrid, { align: 'center' });

    // MATRIX EFFICIENCY VISUALIZATION - Shortcut radar chart
    const chartContainer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 15 }
    });

    const chartTitle = this.scene.rexUI.add.BBCodeText(0, 0, 
      '[b][color=#00DD00]SHORTCUT EFFICIENCY RADAR[/color][/b]', {
      fontSize: '16px',
      fontFamily: "Consolas, monospace",
      resolution: 2
    });
    
    chartContainer.add(chartTitle, { align: 'center' });
    
    const shortcutRadarChart = this.createMatrixSlideWarsRadarChart();
    chartContainer.add(shortcutRadarChart, { align: 'center' });

    contentSizer.add(chartContainer, { proportion: 0, align: 'center' });

    // MATRIX EFFICIENCY ACTIONS
    const efficiencyActions = this.scene.rexUI.add.buttons({
      orientation: 'vertical',
      space: { item: 15 },
      
      buttons: [
        this.scene.createMatrixAchievementButton('⚡ PLAY SHORTCUT MASTERY', 'Continue your keyboard efficiency domination', () => sceneManager.switchToScene("GameScene"), 0x00DD00)
      ]
    });

    contentSizer.add(efficiencyActions, { proportion: 0, align: 'center' });
    contentSizer.layout();
    return contentSizer;
  }

  // Create Matrix Slide Wars Radar Chart
  createMatrixSlideWarsRadarChart() {
    // Calculate EFFICIENCY ACHIEVEMENT metrics (0-100 scale) using REAL data
    const maxScore = Math.max(this.scene.shortcutBestScore || 0, 100000);
    const gamesPlayed = this.scene.shortcutGamesPlayed || 0;
    
    // ACHIEVEMENT-FOCUSED calculations for competitive visualization
    const speed = Math.min(100, ((this.scene.shortcutBestScore || 0) / maxScore) * 100);
    const accuracy = Math.min(100, (gamesPlayed > 0 ? 55 + (this.scene.shortcutBestScore || 0) / 1000 : 25));
    const coverage = Math.min(100, (gamesPlayed > 3 ? 50 + gamesPlayed * 5 : 30));
    const efficiency = Math.min(100, ((this.scene.shortcutBestScore || 0) > 50000 ? 75 : 45));
    const mastery = Math.min(100, (gamesPlayed > 10 ? 70 + Math.random() * 20 : 40));

    // MATRIX color scheme for achievement visualization
    const GetRGBAString = (color, alpha) => {
      if (alpha === undefined) alpha = color.alphaGL;
      return `rgba(${color.red},${color.green},${color.blue},${alpha})`;
    };

    // ACHIEVEMENT MATRIX colors - performance green for visual impact
    const dataColor = Phaser.Display.Color.IntegerToColor(0x00DD00); // Performance green
    const gridColor = Phaser.Display.Color.IntegerToColor(0xFFFFFF); // Matrix white

    return this.scene.rexUI.add.chart(0, 0, 400, 280, {
      type: 'radar',
      
      data: {
        labels: ['Speed', 'Accuracy', 'Coverage', 'Efficiency', 'Mastery'],
        datasets: [{
          label: 'Shortcut Mastery Profile',
          backgroundColor: GetRGBAString(dataColor, 0.3),
          borderColor: GetRGBAString(dataColor, 1),
          pointBackgroundColor: GetRGBAString(dataColor, 1),
          pointBorderColor: '#000000',
          pointBorderWidth: 2,
          borderWidth: 3,
          data: [speed, accuracy, coverage, efficiency, mastery]
        }]
      },
      
      options: {
        plugins: {
          legend: { display: false }
        },
        scales: {
          r: {
            min: 0,
            max: 100,
            ticks: { display: false, stepSize: 25 },
            pointLabels: {
              color: GetRGBAString(gridColor, 1),
              font: { size: 14, family: 'Consolas, monospace' }
            },
            angleLines: { color: GetRGBAString(gridColor, 0.3) },
            grid: { color: GetRGBAString(gridColor, 0.2) }
          }
        }
      }
    });
  }

  // Create Strategic Thinking Page
  createStrategicThinkingPage() {
    const contentSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 25 }
    });

    // Get firm access info
    const firmInfo = this.scene.emailManager.getFirmAccessInfo();
    const userHasFirm = firmInfo.hasAccess;
    const firmName = firmInfo.companyName;

    // Philosophy flow metrics
    const calibrationData = [
      { name: 'Philosophy Peak Score', value: this.scene.philosophyBestScore || 0, color: '#009999', isMainAchievement: true },
      { name: 'Strategic Sessions', value: this.scene.philosophyGamesPlayed || 0, color: '#007777' },
      { name: 'Average Philosophy Score', value: this.scene.philosophyAverageScore || 0, color: '#005555' }
    ];

    if (userHasFirm) {
      calibrationData.push(
        { name: `${firmName} Philosophy Rank`, value: this.scene.philosophyRank || 'Unranked', color: '#FFD700' }
      );
    }

    // Create achievement cards
    const achievementGrid = this.scene.rexUI.add.gridSizer({
      column: 2,
      row: Math.ceil(calibrationData.length / 2),
      columnProportions: [1, 1],
      space: { column: 15, row: 15 }
    });

    calibrationData.forEach(achievement => {
      const displayValue = typeof achievement.value === 'number' ? achievement.value.toLocaleString() : achievement.value;
      const color = achievement.color;
      
      const achievementCard = this.scene.rexUI.add.titleLabel({
        width: 300,
        height: 100,
        
        background: this.scene.rexUI.add.roundRectangle(0, 0, 300, 100, 12, 0x001111, color, 2),
        
        title: this.scene.rexUI.add.BBCodeText(0, 0, `[color=#CCCCCC]${achievement.name}[/color]`, {
          fontSize: '14px',
          fontFamily: 'Consolas, monospace',
          resolution: 2
        }),
        
        text: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=${color}]${displayValue}[/color][/b]`, {
          fontSize: achievement.isMainAchievement ? '24px' : '18px',
          fontFamily: 'Consolas, monospace',
          fontWeight: 'bold',
          resolution: 3
        }),
        
        space: { left: 20, right: 20, top: 15, bottom: 15 },
        align: { title: 'center', text: 'center' }
      });
      
      if (achievement.isMainAchievement) {
        achievementCard.setScale(1.05);
      }
      
      achievementGrid.add(achievementCard);
    });

    achievementGrid.layout();
    contentSizer.add(achievementGrid, { align: 'center' });

    // STRATEGIC THINKING ACTIONS
    const strategyActions = this.scene.rexUI.add.buttons({
      orientation: 'vertical',
      space: { item: 15 },
      
      buttons: [
        this.scene.createMatrixAchievementButton('🧠 PHILOSOPHY FLOW', 'Continue your strategic thinking mastery', () => sceneManager.switchToScene("PhilosophyFlowScene"), 0x009999)
      ]
    });

    contentSizer.add(strategyActions, { proportion: 0, align: 'center' });
    contentSizer.layout();
    return contentSizer;
  }

  // Create Technology Analysis Page
  createTechnologyAnalysisPage() {
    const contentSizer = this.scene.rexUI.add.sizer({
      orientation: 'vertical',
      space: { item: 25 }
    });

    // Get firm access info
    const firmInfo = this.scene.emailManager.getFirmAccessInfo();
    const userHasFirm = firmInfo.hasAccess;
    const firmName = firmInfo.companyName;

    // Tech classification metrics
    const calibrationData = [
      { name: 'Tech Analysis Peak', value: this.scene.classifyTechBestScore || 0, color: '#0088AA', isMainAchievement: true },
      { name: 'Analysis Sessions', value: this.scene.classifyTechGamesPlayed || 0, color: '#0066AA' },
      { name: 'Average Tech Score', value: this.scene.classifyTechAverageScore || 0, color: '#004488' }
    ];

    if (userHasFirm) {
      calibrationData.push(
        { name: `${firmName} Tech Rank`, value: this.scene.classifyTechRank || 'Unranked', color: '#FFD700' }
      );
    }

    // Create achievement cards
    const achievementGrid = this.scene.rexUI.add.gridSizer({
      column: 2,
      row: Math.ceil(calibrationData.length / 2),
      columnProportions: [1, 1],
      space: { column: 15, row: 15 }
    });

    calibrationData.forEach(achievement => {
      const displayValue = typeof achievement.value === 'number' ? achievement.value.toLocaleString() : achievement.value;
      const color = achievement.color;
      
      const achievementCard = this.scene.rexUI.add.titleLabel({
        width: 300,
        height: 100,
        
        background: this.scene.rexUI.add.roundRectangle(0, 0, 300, 100, 12, 0x000A11, color, 2),
        
        title: this.scene.rexUI.add.BBCodeText(0, 0, `[color=#CCCCCC]${achievement.name}[/color]`, {
          fontSize: '14px',
          fontFamily: 'Consolas, monospace',
          resolution: 2
        }),
        
        text: this.scene.rexUI.add.BBCodeText(0, 0, `[b][color=${color}]${displayValue}[/color][/b]`, {
          fontSize: achievement.isMainAchievement ? '24px' : '18px',
          fontFamily: 'Consolas, monospace',
          fontWeight: 'bold',
          resolution: 3
        }),
        
        space: { left: 20, right: 20, top: 15, bottom: 15 },
        align: { title: 'center', text: 'center' }
      });
      
      if (achievement.isMainAchievement) {
        achievementCard.setScale(1.05);
      }
      
      achievementGrid.add(achievementCard);
    });

    achievementGrid.layout();
    contentSizer.add(achievementGrid, { align: 'center' });

    // TECH ANALYSIS ACTIONS
    const techActions = this.scene.rexUI.add.buttons({
      orientation: 'vertical',
      space: { item: 15 },
      
      buttons: [
        this.scene.createMatrixAchievementButton('🔬 CLASSIFY TECH', 'Continue your technology analysis mastery', () => sceneManager.switchToScene("ClassifyScene"), 0x0088AA)
      ]
    });

    contentSizer.add(techActions, { proportion: 0, align: 'center' });
    contentSizer.layout();
    return contentSizer;
  }
}


